import { LoadingButton } from "@mui/lab";
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
	ApprovalTimeEntryStatus,
	ProjectTimeApproval,
	useGetTimeApprovalDetailsLazyQuery,
} from "@repo/server-api";
import { ProjectTimeApprovalExtended } from "@repo/server-api/types";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, { useState, useMemo } from "react";

import {
	validErrorState,
	formatHoursMinutes,
	formatDate,
	formatTimeSpanToHHmm,
} from "@repo/legacy/components/helper/Index";

import ConformationDialogWithContext from "./ConformationWithContext";
import { DataSource } from "../lists/DataSource";

const DialogTimeApprovalDetails = (
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		handleClose?: (value: boolean) => void;
		classes?: string;
		refreshData: (t?: ProjectTimeApprovalExtended) => void;
		timeApproval: ProjectTimeApprovalExtended | null;
		setTimeApproval: (value: ProjectTimeApprovalExtended | null) => void;
		allMyTimeApprovalsDataSource: DataSource<ProjectTimeApproval>;
	}>,
) => {
	const { t, i18n } = useTranslation();

	const timeApproval = useMemo(() => props.timeApproval ?? null, [props.timeApproval]);

	const [getTimeApprovalDetails] = useGetTimeApprovalDetailsLazyQuery({
		fetchPolicy: "no-cache",
		variables: {
			projectTimeApprovalId: timeApproval?.id || "",
		},
		onCompleted(data) {
			if (data.timeApprovalDetails) {
				props.setTimeApproval({
					...timeApproval,
					approvedEntries: data.timeApprovalDetails.entries.map((e) => ({
						...e,
						key: Math.random().toString(36).substring(2, 15),
					})),
				} as ProjectTimeApprovalExtended);
			}
		},
	});

	const open = useMemo(() => {
		if (props.open) getTimeApprovalDetails();
		return props.open;
	}, [props.open]);

	// const [errors, setErrors] = useState<
	// 	Record<string, boolean | object | string>
	// >({});

	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const handleClose = () => {
		props.setOpen(false);
		if (props.handleClose) props.handleClose(false);
		setSaveClicked(false);
		// setErrors({});
		props.setTimeApproval(null);
	};

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};

		// setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		event.stopPropagation();

		const isValid = await validateFields();

		if (isValid && timeApproval?.id) {
			setSaveClicked(true);
			setOpenConformationDialog(true);
		}
	};

	const [openConformationDialog, setOpenConformationDialog] = useState<boolean>(false);
	const [message, setMessage] = useState<string>("");

	return (
		<div>
			<ConformationDialogWithContext
				openPopup={openConformationDialog}
				setOpenDeleteConformation={setOpenConformationDialog}
				text={t("do-you-really-want-to-delete-this-time-approval-sheet")}
				message={message}
				setMessage={setMessage}
				handleYes={() => {
					if (props.allMyTimeApprovalsDataSource.deleteDataWithReason && timeApproval?.id) {
						props.allMyTimeApprovalsDataSource.deleteDataWithReason(timeApproval.id, message);
					}
					setTimeout(() => {
						props.refreshData();
					}, 500);

					setOpenConformationDialog(false);
					props.setOpen(false);
				}}
				handleClose={() => {
					setSaveClicked(false);
					setOpenConformationDialog(false);
				}}
			/>
			<Dialog
				open={open}
				PaperProps={{
					component: "form",
					onSubmit: handleSubmit,
				}}
				maxWidth="md"
				fullWidth={true}>
				<DialogTitle>{t("time-sheet-details")}</DialogTitle>
				<DialogContent>
					<Box sx={{ marginTop: "10px" }}>
						<TextField
							sx={{ width: "40%" }}
							label={t("project-name")}
							value={timeApproval?.project.name || ""}
							variant="outlined"
							InputProps={{
								readOnly: true,
							}}
						/>
						<TextField
							sx={{ width: "40%", float: "right" }}
							label={t("employee")}
							value={timeApproval?.employee.displayName || ""}
							variant="outlined"
							InputProps={{
								readOnly: true,
							}}
						/>
					</Box>
					<Box sx={{ marginTop: "20px", marginBottom: "10px", width: "60%" }}>
						<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language || "de"}>
							<DatePicker
								format={"MMMM YYYY"}
								sx={{ width: "100%", fontColor: "black" }}
								label={t("month")}
								value={timeApproval?.to ? dayjs(timeApproval.to) : null}
								readOnly={true}
							/>
						</LocalizationProvider>
					</Box>
					<DataGridPremium
						getRowHeight={() => "auto"}
						columns={[
							{
								field: "range",
								headerName: t("date-range"),
								flex: 1,
								align: "center",
								headerAlign: "center",
								renderCell: (params) => {
									const date = params.row.date as string;
									const startTime = formatTimeSpanToHHmm(params.row.startTime as string);
									const endTime = formatTimeSpanToHHmm(params.row.endTime as string);
									return (
										<Box sx={{ display: "flex", flexDirection: "column" }}>
											<div>{formatDate(date, i18n.language)}</div>
											<div>
												{startTime} - {endTime}
											</div>
										</Box>
									);
								},
							},
							{
								field: "description",
								headerName: t("description"),
								flex: 1,
								renderCell: (params) => {
									return (
										<Box
											sx={{
												display: "flex",
												justifyContent: "center",
												alignItems: "center",
												height: "100%",
											}}>
											{params.row.description || ""}
										</Box>
									);
								},
							},
							{
								field: "hours",
								headerName: t("hours"),
								flex: 1,
								renderCell: (params) => {
									return (
										<Box
											sx={{
												display: "flex",
												justifyContent: "center",
												alignItems: "center",
												height: "100%",
											}}>
											{formatHoursMinutes(params.row.totalMinutes as number)}
										</Box>
									);
								},
							},
						]}
						rows={timeApproval?.approvedEntries || []}
						getRowId={(row) => row.key as string}
						disableRowSelectionOnClick
						disableColumnMenu
						disableColumnFilter
						disableColumnSelector
						disableDensitySelector
						hideFooter
					/>
					<Box sx={{ marginTop: "20px" }}>
						{t("total-hours")}:{" "}
						{formatHoursMinutes(
							(timeApproval?.approvedEntries ?? []).reduce(
								(acc, entry) => acc + (entry.totalMinutes ?? 0),
								0,
							),
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<LoadingButton
						disabled={timeApproval?.status !== ApprovalTimeEntryStatus.Pending}
						loading={saveClicked}
						type="submit"
						variant="contained"
						className={saveClicked ? "" : "redButton"}>
						{t("delete")}
					</LoadingButton>
					<Button
						data-testid="close-button"
						variant="contained"
						onClick={handleClose}
						className="blueButton">
						{t("cancel")}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};

export default DialogTimeApprovalDetails;
